import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, Upload, ShieldCheck, FileSearch, CheckCircle2, Ban, AlertCircle, FileText, Eye, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,.jpg,.jpeg,.png";
const TIPO_AVISO = "Aviso de privacidad (SOFOM)";

const hoyISO = () => new Date().toISOString().slice(0, 10);

// sep-2026 · REGLA ÚNICA de cuál autorización manda. La usan por igual esta pantalla y
// `SicBloqueOriginacion`, así el encabezado y el formulario NUNCA dicen cosas distintas:
// gana la positiva vigente; si no hay, la última registrada (negativa o vencida).
export function elegirAutorizacion(lista) {
  const hoy = hoyISO();
  const items = (Array.isArray(lista) ? lista : []).filter((a) => a.estatus !== "Reemplazada");
  const vigente = items.find((a) => a.decision !== "no_autoriza" && a.estatus === "Vigente"
    && (!a.vigencia || a.vigencia >= hoy));
  return vigente || items[0] || null;
}

export const autorizacionVigente = (a) => !!a && a.decision !== "no_autoriza"
  && a.estatus === "Vigente" && (!a.vigencia || a.vigencia >= hoyISO());

// Decisión expresa SELLADA para consulta a SIC (Buró/Círculo). El sistema NO genera el consentimiento:
// constata la carta firmada (positivo) o sella la negativa (no autoriza). El Aviso de Privacidad de la
// SOFOM (por entidad, NO de FELVORA) debe constar como presentado SIEMPRE. Reusa /documentos/upload + /sic/*.
//
// feb-2026 (fix hidratación): al montar el componente con `recordId` (cliente/prospecto), consulta
// `/documentos?cliente_id|prospecto_id=recordId` y busca el último "Aviso de privacidad (SOFOM)"
// con estatus Vigente. Si existe, hidrata el estado local con fecha, nombre_archivo y doc_id, y
// muestra el mismo badge que el bloque de identidad ("Ya cargado (fecha) + nombre + ver + Reemplazar").
// Reemplazar usa `reemplaza_doc_id` del backend: 1 solo Vigente, previos quedan Reemplazado con
// reemplazado_por + fecha_reemplazo. Sin acumulación de basura. No se borra nada.
//
// sep-2026 (fix del doble resguardo): la AUTORIZACIÓN también se hidrata. Antes el estado `auth`
// nacía en null y el formulario volvía a pedir la carta aunque ya estuviera resguardada (el
// encabezado sí la veía porque leía `/sic/autorizaciones`). Ahora la verdad llega por la prop
// `autorizacion` (la que ya consulta el padre) y, si no viene, el componente la consulta él mismo.
// Con autorización vigente se muestra "Ya cargado" (medio, fecha, vigencia, ver, Reemplazar) y la
// carga solo reaparece al pulsar Reemplazar, que sube la carta con `reemplaza_doc_id`.
export function SicAutorizacionProspecto({ idField, recordId, sujeto = "prospecto", showConsultar = true, onAuth, autorizacion }) {
  const [via, setVia] = useState("");           // "firmo" | "no_autoriza" | "manual"
  const [file, setFile] = useState(null);
  const [avisoOk, setAvisoOk] = useState(false);
  // Estado del Aviso de privacidad: doc ya persistido en backend (auto-upload).
  const [avisoDocId, setAvisoDocId] = useState(null);
  const [avisoInfo, setAvisoInfo] = useState(null);   // { nombre, tamano, fecha }
  const [avisoBusy, setAvisoBusy] = useState(false);
  const [avisoError, setAvisoError] = useState(null);
  const avisoInputRef = useRef(null);
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const [auth, setAuth] = useState(null);       // decisión sellada en ESTE montaje
  const [autoLocal, setAutoLocal] = useState(null);   // autorización ya resguardada (auto-consulta)
  const [cargandoAuth, setCargandoAuth] = useState(autorizacion === undefined && !!recordId);
  const [reemplazando, setReemplazando] = useState(false);
  const [sociedad, setSociedad] = useState("Círculo de Crédito");
  const [consultando, setConsultando] = useState(false);
  const [resultado, setResultado] = useState(null);
  // Fase 8+ (feb-2026) — 3er camino: "Autoriza por otro medio documentado".
  // El cliente autorizó por un medio distinto a la carta física (correo firmado,
  // grabación de voz, kiosko digital, portal cliente, etc.) y el ejecutivo trae la
  // evidencia. Se captura el medio como texto libre y se sube el PDF de la evidencia
  // (obligatorio, mismo mecanismo que la carta firmada). Reusa POST /sic/autorizacion.
  const [medioManual, setMedioManual] = useState("");   // texto libre: "Correo firmado", "Kiosko", etc.
  const [fileManual, setFileManual] = useState(null);   // PDF evidencia

  // Hidratación: al abrir el formulario, consulta si ya hay un Aviso Vigente del titular
  // (mismo endpoint que DocsIdentidadCliente). Si existe, precarga la vista "Ya cargado".
  const hidratarAvisoExistente = useCallback(async () => {
    if (!recordId) return;
    try {
      const params = idField === "prospecto_id"
        ? { prospecto_id: recordId }
        : { cliente_id: recordId };
      const { data } = await api.get("/documentos", { params });
      const lista = Array.isArray(data) ? data : [];
      const avisos = lista.filter((d) =>
        d.tipo_documento === TIPO_AVISO && d.estatus === "Vigente");
      if (!avisos.length) return;
      // El más reciente por fecha_carga/created_at
      const ultimo = avisos.reduce((a, b) =>
        (String(a.created_at || a.fecha_carga || "") > String(b.created_at || b.fecha_carga || "")) ? a : b);
      setAvisoDocId(ultimo.id);
      setAvisoInfo({
        nombre: ultimo.nombre_archivo || "archivo",
        tamano: ultimo.tamano || 0,
        fecha: ultimo.fecha_carga || (ultimo.created_at || "").slice(0, 10),
      });
    } catch { /* silencioso: si no hay red, la UI queda como si no hubiera aviso */ }
  }, [recordId, idField]);
  useEffect(() => { hidratarAvisoExistente(); }, [hidratarAvisoExistente]);

  // Hidratación de la AUTORIZACIÓN. Si el padre la manda por prop (`autorizacion`, aunque sea
  // null) esa es la fuente única y NO se vuelve a consultar. Si no la manda, se consulta aquí.
  const hidratarAutorizacion = useCallback(async () => {
    if (autorizacion !== undefined) { setCargandoAuth(false); return; }
    if (!recordId) { setAutoLocal(null); setCargandoAuth(false); return; }
    setCargandoAuth(true);
    try {
      const params = idField === "prospecto_id"
        ? { prospecto_id: recordId }
        : { cliente_id: recordId };
      const { data } = await api.get("/sic/autorizaciones", { params });
      setAutoLocal(elegirAutorizacion(data));
    } catch { setAutoLocal(null); }
    finally { setCargandoAuth(false); }
  }, [recordId, idField, autorizacion]);
  useEffect(() => { hidratarAutorizacion(); }, [hidratarAutorizacion]);

  const autorizacionActual = auth || (autorizacion !== undefined ? autorizacion : autoLocal) || null;
  const esNegativa = autorizacionActual?.decision === "no_autoriza";
  const vencida = !!autorizacionActual && !esNegativa && !!autorizacionActual.vigencia
    && autorizacionActual.vigencia < hoyISO();
  const yaResguardada = !!autorizacionActual && !vencida;
  const mostrarFormulario = !yaResguardada || reemplazando;
  // El Aviso de Privacidad ya quedó sellado con la autorización previa: la casilla se
  // muestra marcada y bloqueada (no se vuelve a pedir la misma confirmación).
  const avisoSellado = !!autorizacionActual?.aviso_privacidad_presentado;
  const avisoListo = avisoOk || avisoSellado;
  // Autorización que se va a reemplazar (solo cuando el operador pulsó "Reemplazar").
  const previa = reemplazando ? autorizacionActual : null;

  const tras = (a) => {
    setAuth(a);
    setReemplazando(false);
    setVia("");
    setFile(null);
    setFileManual(null);
    setMedioManual("");
    setNota("");
    onAuth && onAuth();
  };

  const iniciarReemplazo = () => {
    setReemplazando(true);
    setVia("firmo");
    setFile(null);
    setFileManual(null);
    setMedioManual("");
  };

  // AUTO-SUBIDA del Aviso de privacidad al elegir el archivo. Idéntico patrón al de
  // Documentos de identidad: cifrado inmediato, ligado al titular, sobrevive al cierre.
  const subirAvisoAuto = async (f) => {
    if (!f) return;
    if (!recordId) {
      const msg = `Selecciona primero al ${sujeto} para adjuntar el aviso.`;
      setAvisoError(msg);
      toast.error(msg);
      return;
    }
    setAvisoBusy(true);
    setAvisoError(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("tipo_documento", TIPO_AVISO);
      fd.append("categoria", "expediente");
      fd.append("clasificacion", "Soporte");
      fd.append(idField, recordId);
      // Idempotencia: si ya había un aviso previo, backend lo marcará como "Reemplazado".
      if (avisoDocId) fd.append("reemplaza_doc_id", avisoDocId);
      const doc = await api.post("/documentos/upload", fd).then((r) => r.data);
      setAvisoDocId(doc.id);
      setAvisoInfo({
        nombre: doc.nombre_archivo || f.name,
        tamano: doc.tamano || f.size,
        fecha: doc.fecha_carga || new Date().toISOString().slice(0, 10),
      });
      toast.success("Aviso de privacidad cargado y cifrado en el expediente.");
    } catch (e) {
      const msg = apiError(e);
      setAvisoError(msg);
      toast.error(`No se pudo subir el aviso: ${msg}`);
    } finally { setAvisoBusy(false); }
  };

  const abrirDocPrevio = async (docId) => {
    try {
      const { data } = await api.post(`/documentos/${docId}/signed-url`);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const url = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(apiError(e)); }
  };

  const constatar = async () => {
    if (!file) { toast.error("Carga el documento escaneado de la carta firmada."); return; }
    if (!avisoListo) { toast.error("Confirma que se presentó el Aviso de Privacidad de la entidad (SOFOM)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "Carta de autorización SIC (firmada)");
      fd.append("categoria", "expediente");
      fd.append("clasificacion", "Soporte");
      fd.append(idField, recordId);
      // Reemplazo: la carta anterior queda "Reemplazado" y ligada a la nueva (no se borra).
      if (previa?.documento_id) fd.append("reemplaza_doc_id", previa.documento_id);
      const doc = await api.post("/documentos/upload", fd).then((r) => r.data);
      const a = await api.post("/sic/autorizacion", {
        [idField]: recordId, decision: "autoriza", medio: "Carta física firmada",
        documento_id: doc.id, sociedad, aviso_privacidad_presentado: true,
        aviso_privacidad_doc_id: avisoDocId,
        reemplaza_autorizacion_id: previa?.id || null,
      }).then((r) => r.data);
      tras(a);
      toast.success("Autorización física constatada y resguardada (cifrada).");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const registrarNegativa = async () => {
    if (!avisoListo) { toast.error("Confirma que se presentó el Aviso de Privacidad de la entidad (SOFOM)."); return; }
    setBusy(true);
    try {
      const a = await api.post("/sic/autorizacion", {
        [idField]: recordId, decision: "no_autoriza", sociedad, nota,
        aviso_privacidad_presentado: true, aviso_privacidad_doc_id: avisoDocId,
        reemplaza_autorizacion_id: previa?.id || null,
      }).then((r) => r.data);
      tras(a);
      toast.success("Negativa de autorización registrada y sellada. El trámite continúa sin consulta de buró.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  // Fase 8+ (feb-2026) — Registrar autorización por otro medio documentado.
  // Reusa POST /sic/autorizacion con `medio: <capturado>` y `documento_id: <evidencia>`.
  // El backend (integraciones_centro.py) ahora exige documento en TODA autorización
  // positiva (fix del hueco §7), por lo que la evidencia es obligatoria.
  const constatarManual = async () => {
    if (!medioManual.trim()) { toast.error("Captura el medio por el que el cliente autorizó."); return; }
    if (!fileManual) { toast.error("Sube la evidencia (PDF) del medio documentado."); return; }
    if (!avisoListo) { toast.error("Confirma que se presentó el Aviso de Privacidad de la entidad (SOFOM)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fileManual);
      fd.append("tipo_documento", "Evidencia de autorización SIC (medio documentado)");
      fd.append("categoria", "expediente");
      fd.append("clasificacion", "Soporte");
      fd.append(idField, recordId);
      if (previa?.documento_id) fd.append("reemplaza_doc_id", previa.documento_id);
      const doc = await api.post("/documentos/upload", fd).then((r) => r.data);
      const a = await api.post("/sic/autorizacion", {
        [idField]: recordId, decision: "autoriza", medio: medioManual.trim(),
        documento_id: doc.id, sociedad, aviso_privacidad_presentado: true,
        aviso_privacidad_doc_id: avisoDocId,
        reemplaza_autorizacion_id: previa?.id || null,
      }).then((r) => r.data);
      tras(a);
      toast.success("Autorización por medio documentado constatada y resguardada (cifrada).");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const consultar = async () => {
    setConsultando(true);
    try {
      const r = await api.post("/sic/consultar", { [idField]: recordId, sociedad }).then((res) => res.data);
      setResultado(r);
      toast.success(`Consulta ${r.sociedad} realizada (folio ${r.folio}). PDF resguardado en el expediente.`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setConsultando(false); }
  };

  const AvisoCheck = ({ v }) => (
    <label className="mt-2 flex items-start gap-2 text-[11px] text-slate-600" data-testid={`sic-aviso-privacidad-check-${v}`}>
      <input type="checkbox" className="mt-0.5 h-3.5 w-3.5" checked={avisoListo} disabled={avisoSellado}
        onChange={(e) => setAvisoOk(e.target.checked)} data-testid={`sic-aviso-checkbox-${v}`} />
      <span>
        Confirmo que se presentó al {sujeto} el <b>Aviso de Privacidad de la entidad (SOFOM)</b> conforme a la LFPDPPP. (Obligatorio, queda sellado.)
        {avisoSellado && (
          <span className="ml-1 font-medium text-emerald-700" data-testid={`sic-aviso-sellado-${v}`}>
            Ya sellado{autorizacionActual?.fecha ? ` el ${autorizacionActual.fecha}` : ""}.
          </span>
        )}
      </span>
    </label>
  );

  // Bloque del Aviso de privacidad. Cuando NO hay doc cargado: input de archivo con
  // auto-upload al seleccionar. Cuando SÍ hay uno Vigente: badge "Ya cargado" + nombre +
  // fecha + enlace ver + botón "Reemplazar documento" (que dispara el mismo input oculto).
  // El aviso sigue siendo OPCIONAL: no bloquea el sellado ni el guardado, sólo bloquea la
  // SUBIDA cuando no hay titular resolvible.
  const AvisoFile = () => {
    const tieneAviso = !!avisoDocId && !!avisoInfo;
    return (
      <div className="mt-1 space-y-1" data-testid="sic-aviso-file-wrapper">
        <p className="text-[11px] text-slate-400">Aviso de privacidad firmado (opcional, se liga al {sujeto} y se cifra al seleccionarlo):</p>
        {/* Input oculto SIEMPRE presente para poder disparar reemplazo */}
        <input
          ref={avisoInputRef}
          type="file"
          accept={ACCEPT}
          data-testid="sic-aviso-input"
          disabled={avisoBusy || !recordId}
          className={tieneAviso && !avisoBusy ? "hidden" : "block h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"}
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            e.target.value = "";
            if (f) subirAvisoAuto(f);
          }} />
        {!recordId && (
          <p className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="sic-aviso-sin-titular">
            <AlertCircle className="h-3 w-3 shrink-0" />
            Selecciona primero al {sujeto} para poder adjuntar el aviso.
          </p>
        )}
        {avisoBusy && (
          <p className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="sic-aviso-subiendo">
            <Loader2 className="h-3 w-3 animate-spin" /> Subiendo y cifrando…
          </p>
        )}
        {tieneAviso && !avisoBusy && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-800" data-testid="sic-aviso-cargado">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <CheckCircle2 className="h-3 w-3 shrink-0" />
              <span className="font-medium">Ya cargado{avisoInfo.fecha ? ` (${avisoInfo.fecha})` : ""}</span>
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <FileText className="h-3 w-3" /> {avisoInfo.nombre}{avisoInfo.tamano ? ` · ${avisoInfo.tamano} bytes` : ""}
              </span>
              <Button type="button" size="sm" variant="ghost"
                className="h-5 gap-1 px-1.5 text-[11px] text-emerald-700 hover:text-emerald-800"
                data-testid="sic-aviso-ver"
                onClick={() => abrirDocPrevio(avisoDocId)}>
                <Eye className="h-3 w-3" /> ver
              </Button>
              <Button type="button" size="sm" variant="outline"
                className="h-6 gap-1 px-2 text-[11px]"
                data-testid="sic-aviso-reemplazar"
                onClick={() => avisoInputRef.current?.click()}>
                <RotateCcw className="h-3 w-3" /> Reemplazar documento
              </Button>
            </div>
          </div>
        )}
        {avisoError && !avisoBusy && (
          <p className="flex items-center gap-1 text-[11px] font-medium text-red-600" data-testid="sic-aviso-error">
            <AlertCircle className="h-3 w-3 shrink-0" />
            {avisoError} Vuelve a seleccionar el archivo para reintentar.
          </p>
        )}
      </div>
    );
  };

  const OptBtn = ({ value, icon: Icon, children, testid }) => (
    <button type="button" data-testid={testid} onClick={() => setVia(value)}
      className={`flex flex-1 items-start gap-2 rounded-md border p-2.5 text-left text-xs transition-colors ${
        via === value ? "border-primary bg-primary/5 text-slate-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${via === value ? "text-primary" : "text-slate-400"}`} />
      <span>{children}</span>
    </button>
  );

  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid="sic-autorizacion-prospecto">
      <Label className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
        <FileSearch className="h-3.5 w-3.5 text-primary" /> Autorización para consulta a sociedades de información crediticia (Buró/Círculo)
      </Label>

      {cargandoAuth && (
        <p className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="sic-auth-cargando">
          <Loader2 className="h-3 w-3 animate-spin" /> Consultando la autorización resguardada…
        </p>
      )}

      {!cargandoAuth && vencida && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="sic-autorizacion-vencida">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span><b>Autorización vencida el {autorizacionActual.vigencia}</b> ({autorizacionActual.medio}). Registra una nueva autorización del {sujeto}.</span>
        </div>
      )}

      {!cargandoAuth && mostrarFormulario && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <OptBtn value="firmo" icon={ShieldCheck} testid="sic-via-firmo">
            El {sujeto} <b>firmó la carta de autorización</b> para consulta a Buró/Círculo (carta física a puño y letra).
          </OptBtn>
          <OptBtn value="manual" icon={FileText} testid="sic-via-manual">
            El {sujeto} autorizó por <b>otro medio documentado</b> (correo firmado, grabación, kiosko, portal cliente, etc.).
          </OptBtn>
          <OptBtn value="no_autoriza" icon={Ban} testid="sic-via-no">
            <b>No autoriza</b> la consulta a información crediticia. El trámite continúa sin consulta.
          </OptBtn>
        </div>
      )}

      {mostrarFormulario && via === "no_autoriza" && (
        <div className="mt-3 space-y-2" data-testid="sic-no-autoriza-form">
          <p className="text-[11px] text-slate-500">
            Se registrará una <b>negativa expresa sellada</b> (quién, cuándo, IP). No se consultará buró; el trámite continúa.
          </p>
          <AvisoCheck v="no" />
          <AvisoFile />
          <Textarea data-testid="sic-no-autoriza-nota" value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="Nota opcional (motivo de la negativa)" className="h-16 text-xs" />
          <Button data-testid="sic-no-autoriza-btn" size="sm" disabled={busy || !avisoListo} onClick={registrarNegativa} variant="outline">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Ban className="mr-1.5 h-4 w-4" />} Registrar y sellar la negativa
          </Button>
        </div>
      )}

      {mostrarFormulario && via === "firmo" && (
        <div className="mt-3 space-y-2" data-testid="sic-firmo-form">
          <p className="text-[11px] text-slate-500">Carga obligatoria del documento escaneado de la carta firmada. La casilla no queda activa sin el documento.</p>
          <Input type="file" accept={ACCEPT} data-testid="sic-carta-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <AvisoCheck v="firmo" />
          <AvisoFile />
          <Button data-testid="sic-constatar-btn" size="sm" disabled={busy || !file || !avisoListo} onClick={constatar} className="bg-primary hover:bg-sidebar-hover">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Constatar y resguardar autorización
          </Button>
        </div>
      )}

      {mostrarFormulario && via === "manual" && (
        <div className="mt-3 space-y-2" data-testid="sic-manual-form">
          <p className="text-[11px] text-slate-500">
            El cliente autorizó por otro medio y traes la evidencia. Captura el medio y sube el archivo (PDF). Sin evidencia no se puede sellar la autorización.
          </p>
          <Label className="text-[11px] text-slate-600">Medio por el que autorizó el {sujeto}</Label>
          <Input
            data-testid="sic-manual-medio"
            value={medioManual}
            onChange={(e) => setMedioManual(e.target.value)}
            placeholder="Ej.: Correo firmado, grabación de voz, kiosko digital, portal cliente…"
            className="text-sm"
          />
          <Label className="text-[11px] text-slate-600">Evidencia del medio (PDF, obligatorio)</Label>
          <Input
            type="file" accept={ACCEPT}
            data-testid="sic-manual-evidencia"
            onChange={(e) => setFileManual(e.target.files?.[0] || null)}
          />
          <AvisoCheck v="manual" />
          <AvisoFile />
          <Button
            data-testid="sic-manual-btn"
            size="sm"
            disabled={busy || !medioManual.trim() || !fileManual || !avisoListo}
            onClick={constatarManual}
            className="bg-primary hover:bg-sidebar-hover"
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
            Constatar y resguardar autorización
          </Button>
        </div>
      )}

      {reemplazando && (
        <Button type="button" size="sm" variant="ghost" className="mt-2 h-7 px-2 text-[11px] text-slate-500"
          data-testid="sic-reemplazo-cancelar" onClick={() => { setReemplazando(false); setVia(""); setFile(null); }}>
          Cancelar reemplazo
        </Button>
      )}

      {!cargandoAuth && yaResguardada && esNegativa && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="sic-negativa-sellada">
          <div className="flex items-start gap-2">
            <Ban className="mt-0.5 h-4 w-4 shrink-0" />
            <span><b>No autorizó</b> la consulta a Buró/Círculo. Sellado por <b>{autorizacionActual.usuario}</b> el {autorizacionActual.fecha} {autorizacionActual.hora}. Aviso de privacidad presentado. El trámite continúa sin consulta.</span>
          </div>
          {!reemplazando && (
            <Button type="button" size="sm" variant="outline" className="mt-2 h-6 gap-1 px-2 text-[11px]"
              data-testid="sic-negativa-reemplazar" onClick={iniciarReemplazo}>
              <RotateCcw className="h-3 w-3" /> Registrar nueva decisión
            </Button>
          )}
        </div>
      )}

      {!cargandoAuth && yaResguardada && !esNegativa && (
        <div className="mt-3 space-y-2" data-testid="sic-autorizacion-sellada">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span className="font-medium" data-testid="sic-carta-ya-cargado">
                Ya cargado{autorizacionActual.fecha ? ` (${autorizacionActual.fecha})` : ""}
              </span>
              <span>Medio: <b>{autorizacionActual.medio}</b></span>
              <span>Vigencia: <b>{autorizacionActual.vigencia || "—"}</b></span>
              {autorizacionActual.documento_id && (
                <Button type="button" size="sm" variant="ghost"
                  className="h-5 gap-1 px-1.5 text-[11px] text-emerald-700 hover:text-emerald-800"
                  data-testid="sic-carta-ver"
                  onClick={() => abrirDocPrevio(autorizacionActual.documento_id)}>
                  <Eye className="h-3 w-3" /> ver documento
                </Button>
              )}
              {!reemplazando && (
                <Button type="button" size="sm" variant="outline"
                  className="h-6 gap-1 px-2 text-[11px]"
                  data-testid="sic-carta-reemplazar"
                  onClick={iniciarReemplazo}>
                  <RotateCcw className="h-3 w-3" /> Reemplazar documento
                </Button>
              )}
            </div>
            {autorizacionActual.usuario && (
              <p className="mt-1 text-[11px] text-emerald-700">
                Constatada por <b>{autorizacionActual.usuario}</b>
                {autorizacionActual.hora ? ` a las ${autorizacionActual.hora}` : ""}. Carta y aviso de privacidad resguardados (cifrado).
              </p>
            )}
          </div>
          {showConsultar && (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={sociedad} onValueChange={setSociedad}>
                <SelectTrigger data-testid="sic-sociedad-select" className="h-8 w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Círculo de Crédito", "Buró de Crédito", "Ambas (Círculo y Buró)"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid="sic-consultar-btn" size="sm" disabled={consultando} onClick={consultar} className="bg-accent hover:bg-forest-700">
                {consultando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSearch className="mr-1.5 h-4 w-4" />} Consultar Buró / Círculo
              </Button>
            </div>
          )}
          {resultado && (
            <p className="text-[11px] text-slate-600" data-testid="sic-resultado">
              Folio {resultado.folio} · {resultado.sociedad} · {resultado.no_hit ? "Sin información (no hit)" : `Score ${resultado.score} — ${resultado.resultado}`}. PDF en el expediente.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
